import {
  CHANNEL,
  TEMPLATE_LANGUAGE,
  TEMPLATE_STATUS,
  TEMPLATE_TYPE,
} from '../constants/messageTemplateKeys';
import {
  buildDisclosureHtml,
  buildEmailHeaderHtml,
  buildEmailShell,
  buildSenderHtml,
} from './buildEmailShell';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('br').forEach((el) => el.replaceWith(doc.createTextNode('\n')));
  doc.querySelectorAll('a[href]').forEach((a) => {
    a.replaceWith(a.getAttribute('href') ?? '');
  });
  return doc.body.textContent ?? '';
}

// Removes the inline sizes TinyMCE's table resizing bakes onto the layout
// (e.g. width: 69.2076%, height: 812px), which break the imported/mobile layout.
// Applied only to templates that carry their own responsive CSS — see the call site.
// Deliberately surgical so it never touches authored values:
//   • FRACTIONAL width percentages (69.2076%) are stripped from ANY element —
//     authored widths are always whole numbers (65%, 50%, 100%, 600), so only a
//     resize-computed value can be fractional. Whole-number widths, !important,
//     max-width, classes and @media rules are all left intact.
//   • Fixed px heights are stripped only from table/td/th — those layouts size
//     via spacer divs + line-height and never carry a real pixel height on a
//     table element, whereas spacer <div>s legitimately do (so divs are spared).
function stripTinyMceTableSizing(html) {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('[style], [width]').forEach((el) => {
    const isTableEl = /^(table|td|th)$/i.test(el.tagName);
    const style = el.getAttribute('style');
    if (style) {
      const cleaned = style
        .split(';')
        .map((decl) => decl.trim())
        .filter(Boolean)
        .filter((decl) => {
          const idx = decl.indexOf(':');
          if (idx === -1) return true;
          const prop = decl.slice(0, idx).trim().toLowerCase();
          const val = decl
            .slice(idx + 1)
            .trim()
            .toLowerCase();
          if (prop === 'width' && /^\d+\.\d+%/.test(val)) return false;
          if (isTableEl && prop === 'height' && /^\d+(\.\d+)?px$/.test(val)) return false;
          return true;
        })
        .join('; ');
      if (cleaned) el.setAttribute('style', cleaned);
      else el.removeAttribute('style');
    }
    // Fractional width attribute (width="69.2076%"); whole-number/px attrs stay.
    const widthAttr = el.getAttribute('width');
    if (widthAttr && /^\d+\.\d+%$/.test(widthAttr.trim())) el.removeAttribute('width');
  });
  return doc.body.innerHTML;
}

function addTargetBlankToLinks(html) {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noreferrer');
  });
  return doc.documentElement.outerHTML;
}

function normalizeId(value) {
  const raw = String(value ?? '').trim();
  if (raw.length === 0) return null;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
  return null;
}

function normalizeIdList(values) {
  if (!Array.isArray(values)) return [];
  return values.map(normalizeId).filter((value) => value != null);
}

function normalizeSubjects(subjects) {
  if (!Array.isArray(subjects)) return [];
  return subjects
    .map((text) => String(text ?? '').trim())
    .filter((text) => text.length > 0)
    .map((text) => ({ text }));
}

// One trigger + N send-condition rows → flat list of { trigger, send_condition, day }
// entries (the backend templatetrigger_set contract). Values are sent as entered so
// the backend validation reports any problems on save.
function normalizeTriggers(form) {
  const triggerId = normalizeId(form?.triggerId);
  if (triggerId == null) return [];
  const rows = Array.isArray(form?.sendConditions) ? form.sendConditions : [];
  return rows.map((row) => {
    const dayRaw = String(row?.day ?? '').trim();
    return {
      trigger: triggerId,
      send_condition: String(row?.sendCondition ?? ''),
      day: dayRaw.length ? Number(dayRaw) : null,
    };
  });
}

// ─── Payload builder ──────────────────────────────────────────────────────────

export function buildCreateTemplatePayload({
  channel = CHANNEL.EMAIL,
  status = TEMPLATE_STATUS.DRAFT,
  form = {},
  editorContent = '',
  customStyle = '',
  logoUrl = '',
  checkedDisclosures = [],
  insertedVariableIds = [],
  senderInfo = null,
  includeLanguage = true,
} = {}) {
  const isEmail = String(channel ?? CHANNEL.EMAIL) === CHANNEL.EMAIL;
  const isMms = String(channel ?? '') === CHANNEL.MMS;
  const isTrigger = String(form?.type ?? '') === TEMPLATE_TYPE.TRIGGER;
  const type = isTrigger ? TEMPLATE_TYPE.TRIGGER : TEMPLATE_TYPE.MARKETING;

  const headerHtml = isEmail ? buildEmailHeaderHtml(logoUrl) : '';
  const disclosureHtml = isEmail ? buildDisclosureHtml(checkedDisclosures) : '';
  const senderHtml = isEmail ? buildSenderHtml(senderInfo) : '';

  // These sizes only do damage when they fight a template's own responsive rules, which is what
  // migrated (MJML) templates carry in customStyle. Elsewhere they are the author's cell sizing.
  const hasOwnResponsiveCss = String(customStyle ?? '').trim().length > 0;
  const bodyHtml = String(editorContent ?? '');

  return {
    name: String(form?.templateName ?? '').trim(),
    type,
    channel: String(channel ?? CHANNEL.EMAIL),
    ...(includeLanguage ? { language: String(form?.language ?? TEMPLATE_LANGUAGE.ENGLISH) } : {}),
    status: String(status ?? TEMPLATE_STATUS.DRAFT),
    html_content: isEmail
      ? addTargetBlankToLinks(
          buildEmailShell(
            headerHtml,
            hasOwnResponsiveCss ? stripTinyMceTableSizing(bodyHtml) : bodyHtml,
            disclosureHtml,
            senderHtml,
            customStyle,
          ),
        )
      : '',
    text_content: isEmail ? '' : stripHtml(bodyHtml),
    campaigns: normalizeIdList(form?.selectedCampaignIds),
    tags: normalizeIdList(form?.selectedTagIds),
    triggers: isTrigger ? normalizeTriggers(form) : [],
    subjects: isEmail ? normalizeSubjects(form?.subjects) : [],
    disclosures: checkedDisclosures.map((d) => normalizeId(d?.id)).filter((id) => id != null),
    variables: normalizeIdList(insertedVariableIds),
    attachments: isMms && logoUrl ? [logoUrl] : [],
  };
}
